import React, { useEffect, useMemo, useState } from 'react';
import {
  Container, Row, Col, Input, Button, Badge, Card, CardBody
} from 'reactstrap';
import serverRequest from '../../../utils/serverRequest';
import './Atena.css';
import TheHeader from "../../../containers/TheHeader";
import TheFooter from "../../../containers/TheFooter";

/* ===================== PALETA ===================== */
const palette = {
  cardBg: '#00000000',
  cardBgHover: '#162243',
  cardBorder: '#1c2a57',
  text: '#eaeefb',
  subText: '#b9c2f2',
  inputBg: '#000',
  inputBorder: '#2b3f7a',
  primary: '#6c5ce7',
  danger: '#e74c3c',
  success: '#2ecc71',
  warning: '#f1c40f',
  info: '#3498db'
};

/* ===================== HELPERS ===================== */
function useQuery() {
  const hash = window.location.hash || '';
  const qstr = hash.includes('?') ? hash.split('?')[1] : '';
  const obj = {};
  qstr.split('&').forEach(p => {
    const [k, v] = p.split('=');
    if (k) obj[decodeURIComponent(k)] = decodeURIComponent(v || '');
  });
  return obj;
}

function getStatusBadgeStyle(status) {
  const map = {
    'Aberto':      { color: palette.danger,  bg: 'rgba(231,76,60,.2)' },
    'Em Atendimento': { color: palette.warning, bg: 'rgba(241,196,15,.2)' },
    'Resolvido':   { color: palette.success, bg: 'rgba(46,204,113,.2)' },
    'Cancelado':  { color: palette.subText, bg: 'rgba(185,194,242,.15)' }
  };
  const s = map[status] || { color: palette.info, bg: 'rgba(52,152,219,.2)' };
  return { color: s.color, backgroundColor: s.bg, fontWeight: 700 };
}

/* ===================== COMPONENTE ===================== */
export default function AtenaMeusChamados() {
  const query = useQuery();
  const mine = String(query.mine || '1') === '1';

  const [lista, setLista] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [busca, setBusca] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const pageSize = 20;
  const statusOpts = useMemo(
    () => ['Todos', 'Aberto', 'Em Atendimento','Aguardando Retorno' ,'Resolvido', 'Cancelado'],
    []
  );

  /* ===================== LOAD ===================== */
  useEffect(() => {
    let abort = false;

    async function load() {
      setLoading(true);

      const body = {
        mine,
        status: status && status !== 'Todos' ? status : undefined,
        q: busca || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize
      };

      try {
        const resp = await serverRequest.request(
          '/atena/chamados/listar',
          body
        );

        console.log('[AtenaMeusChamados] resposta API:', resp);

        if (!abort) {
          setLista(Array.isArray(resp?.tickets) ? resp.tickets : []);
          setTotal(resp?.total || 0);
        }
      } catch (err) {
        console.error('[AtenaMeusChamados] erro:', err);
        if (!abort) {
          setLista([]);
          setTotal(0);
        }
      } finally {
        if (!abort) setLoading(false);
      }
    }

    load();
    return () => { abort = true; };
  }, [status, busca, page, mine]);

  /* ===================== STYLES ===================== */
  const pageStyle = {
    minHeight: '100vh',
    backgroundImage: "url('/img/bg-gorila.png')",
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    padding: '2rem 1rem'
  };

  const cardStyle = {
    color: palette.text,
    border: `1px solid ${palette.cardBorder}`,
    borderRadius: 12,
    marginBottom: 16,
    padding: 20,
    cursor: 'pointer',
    transition: 'all .3s'
  };

  /* ===================== RENDER ===================== */
  return (
    <>
      <TheHeader />

      <div style={pageStyle}>
        <Container>
          <Row>
            <Col lg={12}>

              {/* HEADER */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 25 }}>
                <h1 style={{ color: palette.text, flexGrow: 1 }}>Meus Chamados</h1>
                <Button
                  outline
                  color="secondary"
                  onClick={() => window.location.hash = '#/'}
                >
                  ← Voltar
                </Button>
              </div>

              {/* FILTROS */}
              <Card style={{ background: palette.cardBg, border: `1px solid ${palette.cardBorder}`, marginBottom: 25 }}>
                <CardBody>
                  <Row>
                    <Col md={6}>
                      <Input
                        placeholder="🔎 Buscar..."
                        value={busca}
                        onChange={e => { setBusca(e.target.value); setPage(1); }}
                      />
                    </Col>
                    <Col md={4}>
                      <Input
                        type="select"
                        value={status}
                        onChange={e => { setStatus(e.target.value); setPage(1); }}
                      >
                        {statusOpts.map(s => (
                          <option key={s} value={s === 'Todos' ? '' : s}>{s}</option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={2} className="text-right">
                      <span style={{ color: palette.subText }}>
                        {total} registros
                      </span>
                    </Col>
                  </Row>
                </CardBody>
              </Card>

              {/* LISTA */}
              {loading ? (
                <p style={{ color: palette.subText, textAlign: 'center' }}>
                  Carregando chamados...
                </p>
              ) : lista.length === 0 ? (
                <p style={{ color: palette.subText, textAlign: 'center' }}>
                  Nenhum chamado encontrado.
                </p>
              ) : (
                lista.map(r => (
                  <a
                    key={r.id_ticket}
                    href={`#/atena/tratamento/${r.id_ticket}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    <div
                      style={cardStyle}
                      onMouseEnter={e => e.currentTarget.style.background = palette.cardBgHover}
                      onMouseLeave={e => e.currentTarget.style.background = palette.cardBg}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', gap: 15 }}>
                          <Badge pill style={getStatusBadgeStyle(r.status)}>
                            {r.status}
                          </Badge>
                          <b style={{ color: palette.primary }}>
                            #{r.id_ticket}
                          </b>
                        </div>
                        <strong>{r.titulo}</strong>
                      </div>

                      <div style={{ marginTop: 8, color: palette.subText }}>
                        🔥 Prioridade: <b>{r.prioridade}</b> &nbsp;|&nbsp;
                        👤 Criado por: {r.criado_por}
                      </div>
                    </div>
                  </a>
                ))
              )}

              {/* PAGINAÇÃO */}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 15, marginTop: 25 }}>
                <Button
                  outline
                  disabled={page <= 1 || loading}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  ← Anterior
                </Button>

                <span style={{ color: palette.subText }}>
                  Página {page}
                </span>

                <Button
                  outline
                  disabled={page * pageSize >= total || loading}
                  onClick={() => setPage(p => p + 1)}
                >
                  Próxima →
                </Button>
              </div>

            </Col>
          </Row>
        </Container>
      </div>

      <TheFooter />
    </>
  );
}
